import { useQuery } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import React, { useState } from 'react'

import type { Theme } from '@material-ui/core'
import { Card, makeStyles } from '@material-ui/core'

import type { ValueFilter } from 'json-to-lucene'

import { Button, useDialogs } from '@paintscout/ui'

import NewReferralCodeDialog from 'src/components/NewReferralCodeDialog'
import { useSnackbar } from 'notistack'
import type { ClientMetaDocument } from 'paintscout'
import { NavHeader } from '../../components/NavDrawer'
import ErrorMessage from '../../components/ErrorMessage'
import CouponCodeTable from '../../components/CouponCodeTable'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {},
  tableWrapper: {
    marginTop: theme.spacing(2)
  },
  searchBar: {
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1, 0),
      gridColumn: '1 / 3'
    }
  },
  searchContainer: {
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start'
    },
    '& input': {
      minWidth: 400,
      [theme.breakpoints.down('md')]: {
        minWidth: 260
      },
      [theme.breakpoints.down('sm')]: {
        minWidth: 'unset'
      }
    }
  },
  filterChips: {
    '&$filterChips': {
      gridRow: 3,
      gridColumn: '1 / -1',
      justifyContent: 'flex-start',
      marginLeft: theme.spacing(-1)
    }
  }
}))

export interface SearchProps {
  text: string
  filters: ValueFilter[]
  isInitialSearch?: boolean
}

export interface UrlParams {
  sort: string
  page: number
  search: string
}

export default function ListCouponCodes() {
  const classes = useStyles({})
  const { enqueueSnackbar } = useSnackbar()
  const { openDialog, dismissDialog } = useDialogs()
  const [rows, setRows] = useState([])

  const { loading, error } = useQuery(
    gql`
      query coupons($prefix: String, $status: String) {
        coupons(prefix: $prefix, status: $status) {
          id
          status
          name
          email
          redemptions
        }
      }
    `,
    {
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: false,
      variables: {
        prefix: 'Referral - '
      },
      onCompleted(data) {
        setRows(data?.coupons ?? [])
      }
    }
  )

  const handleTextClick = (rowId: string, client: Partial<ClientMetaDocument>) => {
    navigator.clipboard.writeText(client[rowId])
    enqueueSnackbar(`${rowId[0].toUpperCase() + rowId.slice(1)} Copied`, { variant: 'success' })
  }

  return (
    <div className={classes.root}>
      <NavHeader title="Referral Partners" />
      <Button
        color="primary"
        variant="text"
        onClick={() => {
          openDialog(NewReferralCodeDialog, {
            title: 'New Referral Coupon',
            prefix: 'Referral - ',
            onClose: dismissDialog,
            onConfirm: (newCoupon) => {
              setRows((rows) => [newCoupon, ...rows])
              dismissDialog()
            }
          })
        }}
      >
        + New Referral Coupon
      </Button>

      {error && <ErrorMessage>{error.message}</ErrorMessage>}

      <Card className={classes.tableWrapper}>
        <CouponCodeTable
          onClientClick={handleTextClick}
          rows={
            loading
              ? []
              : rows.map((row) => {
                  return {
                    ...row,
                    code: row.id
                  }
                })
          }
          loading={loading}
        />
      </Card>
    </div>
  )
}
